import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Renders long grids incrementally so big pages (242 Pals, 200+ TCG cards)
 * hydrate and paint fast. The first `step` items render immediately (so key
 * content is in the SSR HTML for crawlers) and the rest append only when the
 * sentinel scrolls into view or the user requests more.
 */
export function useIncrementalList<T>(items: T[], step = 60) {
  const [limit, setLimit] = useState(step);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Reset when the underlying result set changes (search/filter).
  useEffect(() => setLimit(step), [items, step]);

  const grow = useCallback(
    (by = step) => setLimit((l) => Math.min(l + by, items.length)),
    [items.length, step]
  );

  // Sentinel-driven growth: expands well before the user reaches the bottom.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || limit >= items.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) grow();
      },
      { rootMargin: "1200px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [limit, items.length, grow]);

  const visible = useMemo(() => items.slice(0, limit), [items, limit]);
  return {
    visible,
    hasMore: limit < items.length,
    remaining: Math.max(0, items.length - limit),
    showMore: () => grow(),
    sentinelRef,
  };
}
